import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  ListItemText,
  OutlinedInput,
  Chip,
} from '@mui/material'

// Componentes personalizados
import PlanificacionTable from 'src/components/PlanificacionTable/PlanificacionTable'
import PlanificacionChart from 'src/components/PlanificacionChart/PlanificacionChart'

// Hook personalizado
import { useResumenCompraDetalle } from 'src/hooks/useResumenCompraDetalle'

const STORAGE_KEY_SEGMENTOS = 'resumenCompra_segmentosSeleccionados'
const STORAGE_KEY_ESTADOS = 'resumenCompra_estadosSeleccionados'
const STORAGE_KEY_SEGMENTOS_DISPONIBLES = 'resumenCompra_segmentosDisponibles'
const STORAGE_KEY_ESTADOS_DISPONIBLES = 'resumenCompra_estadosDisponibles'

const ResumenCompraDetalle = ({
  initialParams,
  escenarioSeleccionado = 'inicial_2025_6',
  tipoNegocio = 'TVS',
  setTipoNegocio,
}) => {
  const {
    showAlert,
    alertMessage,
    companiaSelected,
    companiasDisponibles,
    loadingCompanias,
    tablaData,
    loadingTabla,
    mesesColumnas,
    tipoPeriodo,
    prototipos,
    segundas,
    tiposPeriodoDisponibles,
    segmentosSeleccionados,
    estadosSeleccionados,
    setSegmentosSeleccionados,
    setEstadosSeleccionados,
    setPrototipos,
    setSegundas,
    handleCompaniaChange,
    handleTipoPeriodoChange,
    handleAplicarFiltros,
    handleCloseAlert,
    refetchTablaData,
    sellInResumen,
    kardexTecnoSuperActivo,
  } = useResumenCompraDetalle(initialParams, escenarioSeleccionado, tipoNegocio, setTipoNegocio)

  // Estados para las opciones disponibles de filtros
  const [segmentosDisponibles, setSegmentosDisponibles] = useState([])
  const [estadosDisponibles, setEstadosDisponibles] = useState([])

  // Cargar opciones disponibles desde localStorage
  useEffect(() => {
    try {
      const storedSegmentosDisponibles = localStorage.getItem(
        STORAGE_KEY_SEGMENTOS_DISPONIBLES,
      )
      const storedEstadosDisponibles = localStorage.getItem(
        STORAGE_KEY_ESTADOS_DISPONIBLES,
      )

      if (storedSegmentosDisponibles) {
        const segmentosParsed = JSON.parse(storedSegmentosDisponibles)
        setSegmentosDisponibles(
          Array.isArray(segmentosParsed) ? segmentosParsed : [],
        )
      }

      if (storedEstadosDisponibles) {
        const estadosParsed = JSON.parse(storedEstadosDisponibles)
        setEstadosDisponibles(Array.isArray(estadosParsed) ? estadosParsed : [])
      }
    } catch (error) {
      console.error('Error al cargar opciones de filtros:', error)
    }
  }, [])

  // Escuchar cambios en las opciones disponibles desde localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY_SEGMENTOS_DISPONIBLES && e.newValue) {
        try {
          const segmentosParsed = JSON.parse(e.newValue)
          setSegmentosDisponibles(
            Array.isArray(segmentosParsed) ? segmentosParsed : [],
          )
        } catch (error) {
          console.error(
            'Error al parsear segmentos disponibles desde storage:',
            error,
          )
        }
      }
      if (e.key === STORAGE_KEY_ESTADOS_DISPONIBLES && e.newValue) {
        try {
          const estadosParsed = JSON.parse(e.newValue)
          setEstadosDisponibles(
            Array.isArray(estadosParsed) ? estadosParsed : [],
          )
        } catch (error) {
          console.error(
            'Error al parsear estados disponibles desde storage:',
            error,
          )
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)

    // También escuchar cambios locales usando un evento personalizado
    const handleLocalStorageChange = (e) => {
      if (e.detail.key === STORAGE_KEY_SEGMENTOS_DISPONIBLES) {
        setSegmentosDisponibles(
          Array.isArray(e.detail.value) ? e.detail.value : [],
        )
      }
      if (e.detail.key === STORAGE_KEY_ESTADOS_DISPONIBLES) {
        setEstadosDisponibles(
          Array.isArray(e.detail.value) ? e.detail.value : [],
        )
      }
    }

    window.addEventListener('localStorageChange', handleLocalStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('localStorageChange', handleLocalStorageChange)
    }
  }, [])

  // Función para sincronizar cambios de filtros con localStorage y notificar a otros componentes
  const actualizarFiltros = (tipo, nuevosValores) => {
    const storageKey =
      tipo === 'segmentos' ? STORAGE_KEY_SEGMENTOS : STORAGE_KEY_ESTADOS
    const setter =
      tipo === 'segmentos' ? setSegmentosSeleccionados : setEstadosSeleccionados

    setter(nuevosValores)
    localStorage.setItem(storageKey, JSON.stringify(nuevosValores))

    // Emitir evento personalizado para sincronización en la misma pestaña
    window.dispatchEvent(
      new CustomEvent('localStorageChange', {
        detail: { key: storageKey, value: nuevosValores },
      }),
    )
  }

  const handleSegmentosChange = (event) => {
    const {
      target: { value },
    } = event
    const nuevosValores = typeof value === 'string' ? value.split(',') : value
    actualizarFiltros('segmentos', nuevosValores)
  }

  const handleEstadosChange = (event) => {
    const {
      target: { value },
    } = event
    const nuevosValores = typeof value === 'string' ? value.split(',') : value
    actualizarFiltros('estados', nuevosValores)
  }

  // Mostrar loading mientras cargan las compañías
  if (loadingCompanias) {
    return (
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', p: 3 }}>
        <Typography>Cargando compañías...</Typography>
      </Paper>
    )
  }

  // Mostrar mensaje cuando no hay compañías disponibles
  if (companiasDisponibles.length === 0) {
    return (
      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No hay compañías disponibles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No se encontraron compañías disponibles.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      {/* Información del tipo de negocio seleccionado */}
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <FormControl
            size="small"
            variant="outlined"
            sx={{ minWidth: 160, width: { xs: '100%', sm: 200 } }}
          >
            <InputLabel id="resumen-flujo-compania-label">Compañía</InputLabel>
            <Select
              labelId="resumen-flujo-compania-label"
              id="resumen-flujo-compania"
              value={companiaSelected}
              onChange={handleCompaniaChange}
              label="Compañía"
              displayEmpty
            >
              <MenuItem value="">
                <em>Selecciona una compañía</em>
              </MenuItem>
              {companiasDisponibles.map((compania) => (
                <MenuItem key={compania} value={compania}>
                  {compania}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Loading estado */}
        {loadingTabla && (
          <Box sx={{ textAlign: 'center', p: 2 }}>
            <Typography>Cargando datos...</Typography>
          </Box>
        )}

        {/* Contenido principal - solo se muestra cuando hay compañía seleccionada y no está cargando */}
        {companiaSelected && !loadingTabla && (
          <>
            {/* Selector de período y filtros */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 1.5,
                flexWrap: 'wrap',
                columnGap: 1.5,
                rowGap: 1,
              }}
            >
              {/* Selector de segmentos */}
              {segmentosDisponibles.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    flex: '1 1 100%',
                    width: '100%',
                    marginTop: '10px',
                  }}
                >
                  <FormControl
                    size="small"
                    variant="outlined"
                    sx={{ flex: 1, minWidth: 260 }}
                  >
                    <InputLabel>Segmentos</InputLabel>
                    <Select
                      multiple
                      value={segmentosSeleccionados}
                      onChange={handleSegmentosChange}
                      input={<OutlinedInput label="Segmentos" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 0.5,
                            py: 0.25,
                            overflow: 'hidden',
                          }}
                        >
                          {selected.map((value) => (
                            <Chip
                              key={value}
                              label={value}
                              size="small"
                              sx={{ maxWidth: 160, flexShrink: 0 }}
                            />
                          ))}
                        </Box>
                      )}
                    >
                      {segmentosDisponibles.map((segmento) => (
                        <MenuItem key={segmento} value={segmento}>
                          <Checkbox
                            checked={
                              segmentosSeleccionados.indexOf(segmento) > -1
                            }
                          />
                          <ListItemText primary={segmento} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    {segmentosSeleccionados.length} /{' '}
                    {segmentosDisponibles.length}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        actualizarFiltros('segmentos', segmentosDisponibles)
                      }
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    >
                      Todo
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ alignSelf: 'center' }}
                    >
                      |
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => actualizarFiltros('segmentos', [])}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#757575',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      Limpiar
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Selector de estados */}
              {estadosDisponibles.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    flex: '1 1 360px',
                    width: '100%',
                    marginTop: '10px',
                    marginBottom: '10px',
                  }}
                >
                  <FormControl size="small" variant="outlined" sx={{ minWidth: 260, maxWidth: 360, width: '100%' }}>
                    <InputLabel>Estados</InputLabel>
                    <Select
                      multiple
                      value={estadosSeleccionados}
                      onChange={handleEstadosChange}
                      input={<OutlinedInput label="Estados" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                          {selected.map((value) => (
                            <Chip key={value} label={value} size="small" />
                          ))}
                        </Box>
                      )}
                    >
                      {estadosDisponibles.map((estado) => (
                        <MenuItem key={estado} value={estado}>
                          <Checkbox
                            checked={estadosSeleccionados.indexOf(estado) > -1}
                          />
                          <ListItemText primary={estado} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    {estadosSeleccionados.length} / {estadosDisponibles.length}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        actualizarFiltros('estados', estadosDisponibles)
                      }
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#1976d2',
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        },
                      }}
                    >
                      Todo
                    </Button>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ alignSelf: 'center' }}
                    >
                      |
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => actualizarFiltros('estados', [])}
                      sx={{
                        textTransform: 'none',
                        fontSize: '13px',
                        color: '#757575',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      Limpiar
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <FormControl
                size="small"
                variant="outlined"
                sx={{ minWidth: 140, width: { xs: '100%', sm: 160 } }}
              >
                <InputLabel id="resumen-flujo-periodo-label">Período</InputLabel>
                <Select
                  labelId="resumen-flujo-periodo-label"
                  id="resumen-flujo-periodo"
                  value={tipoPeriodo}
                  onChange={handleTipoPeriodoChange}
                  label="Período"
                >
                  {tiposPeriodoDisponibles.map((periodo) => (
                    <MenuItem key={periodo.value} value={periodo.value}>
                      {periodo.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={prototipos}
                    onChange={(e) => setPrototipos(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Prototipos
                  </Typography>
                }
                sx={{ ml: 0.5, mr: 0 }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={segundas}
                    onChange={(e) => setSegundas(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Segundas
                  </Typography>
                }
                sx={{ ml: 0.5, mr: 0 }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAplicarFiltros}
                disabled={!companiaSelected}
                sx={{
                  ml: 1,
                  backgroundColor: '#4c75aa',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#3f6594',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                }}
              >
                Filtrar
              </Button>
            </Box>

            {/* Título de la compañía */}
            {companiaSelected && (
              <p
                style={{
                  fontSize: '16px',
                  fontWeight: 'bold',
                  fontFamily: 'inherit',
                }}
              >
                {companiaSelected}
              </p>
            )}

            {/* Tarjetas Sell In T-1, Sell In T, Variación Sell In % - siempre visibles */}
            <Box
            style={{
              marginBottom: '15px',
              width: '500px',
            }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2,
                mb: 3,
              }}
            >
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 1,
                      fontSize: '0.7rem',
                      lineHeight: 1.1,
                    }}
                  >
                    Sell In T-1
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mt: 0.25,
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  >
                    {sellInResumen.sell_in_anio_anterior != null
                      ? Number(sellInResumen.sell_in_anio_anterior).toLocaleString('es-CO')
                      : '-'}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 1,
                      fontSize: '0.7rem',
                      lineHeight: 1.1,
                    }}
                  >
                    Sell In T
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mt: 0.25,
                      fontFamily: 'monospace',
                      fontSize: '14px',
                    }}
                  >
                    {sellInResumen.sell_in_anio_actual != null
                      ? Number(sellInResumen.sell_in_anio_actual).toLocaleString('es-CO')
                      : '-'}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    background:
                      sellInResumen.variacion_anual == null
                        ? 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                        : sellInResumen.variacion_anual >= 0
                          ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                          : 'linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)',
                    transition: 'box-shadow 0.2s ease',
                    '&:hover': { boxShadow: 1 },
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: 1,
                      fontSize: '0.7rem',
                      lineHeight: 1.1,
                    }}
                  >
                    Variación Sell In %
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mt: 0.25,
                      fontFamily: 'monospace',
                      fontSize: '14px',
                      color:
                        sellInResumen.variacion_anual != null
                          ? sellInResumen.variacion_anual >= 0
                            ? 'success.main'
                            : 'error.main'
                          : 'text.primary',
                    }}
                  >
                    {sellInResumen.variacion_anual != null
                      ? `${Number(sellInResumen.variacion_anual).toLocaleString('es-CO', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}%`
                      : '-'}
                  </Typography>
                </Paper>
            </Box>

            {/* Tabla principal - Solo lectura, sin funcionalidades de edición */}
            <PlanificacionTable
              tablaData={tablaData}
              mesesColumnas={mesesColumnas}
              tipoPeriodo={tipoPeriodo}
              modeloPlaneacion={null}
              escenario={escenarioSeleccionado}
              onDataRefresh={refetchTablaData}
              readOnly
              tipoNegocio={companiaSelected}
              mostrarKardexTecnoSuper={kardexTecnoSuperActivo}
              // No pasar onLlegadasButtonClick ni onSellInButtonClick para deshabilitar edición
            />

            {/* Gráfica de Planificación */}
            <PlanificacionChart
              tablaData={tablaData}
              mesesColumnas={mesesColumnas}
              tipoPeriodo={tipoPeriodo}
            />
          </>
        )}
      </Box>

      {/* Snackbar para notificaciones */}
      <Snackbar
        open={showAlert}
        autoHideDuration={3000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity="success"
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default ResumenCompraDetalle
